import { DragEvent, useState } from 'react';

import styled from 'styled-components';

import { FileAcceptText } from '@components/file-field/file-accept-text';
import { Stack } from '@components/stack';
import { Text } from '@components/text';
import { useScopedTranslate } from '@locale/translate';
import { theme } from '@styles/theme';
import { pxToRem } from '@utils/converters';

import { editableCellLocale } from './locale';

export type EditableCellDropzoneProps = {
  accept?: string;
  isMulti: boolean;
  onFilesPicked(files: Array<File>): void;
  onBrowse(): void;
};

export const EditableCellDropzone = ({
  accept,
  isMulti,
  onFilesPicked,
  onBrowse,
}: EditableCellDropzoneProps) => {
  const { t } = useScopedTranslate(editableCellLocale);
  const [isDraggedOver, setIsDraggedOver] = useState(false);

  const handleDrop = (event: DragEvent<HTMLButtonElement>) => {
    event.preventDefault();
    setIsDraggedOver(false);
    const files = Array.from(event.dataTransfer.files);
    if (files.length > 0) {
      onFilesPicked(isMulti ? files : [files[0]]);
    }
  };

  return (
    <Container
      data-dragged-over={isDraggedOver}
      data-c="editable-cell-dropzone"
      onClick={onBrowse}
      onDragOver={(event) => {
        event.preventDefault();
        setIsDraggedOver(true);
      }}
      onDragLeave={() => setIsDraggedOver(false)}
      onDrop={handleDrop}
    >
      <Stack space={1} align="center">
        <Text font="regular14" color="elementBlue100" text={t('dropzone')} />
        <FileAcceptText accept={accept} />
      </Stack>
    </Container>
  );
};

const Container = styled.button.attrs({ type: 'button' })({
  display: 'flex',
  justifyContent: 'center',
  width: '100%',
  // The designs draw the drop area at 100px tall, which is the height these
  // paddings give it around the single line of drop copy.
  padding: `${pxToRem(40)} ${pxToRem(8)}`,
  borderRadius: theme.form.border.radius,
  borderWidth: theme.form.border.width,
  borderStyle: 'dashed',
  borderColor: theme.color.border20,
  background: 'transparent',
  cursor: 'pointer',

  '&:hover, &[data-dragged-over="true"]': {
    borderColor: theme.color.elementBlue100,
    background: theme.color.blueTrans5,
  },
});
